// Must-win categories — the spine of the supply/demand pages.
//
// The taxonomy is anchored on what the `coinsLedger` narration classifier
// actually produces (see funnel-review-standalone.html · Cut 1). We do NOT
// invent aspirational categories; the surface measures what is real.
//
// Each category carries:
//   - a snapshot of live metrics (12 May 2026, from the funnel review) —
//     this is replaced by per-category SQL queries in Phase 3.
//   - a derived `fit` score against the fee-defense pattern: recurring +
//     fiat-linked at the moment of value + small-denomination. The function
//     `deriveFit()` makes the rule auditable; the ManCo can argue with the
//     thresholds, not with a hand-coded label.
//   - the primary ManCo owner (from the Action Tracker 20.5.26 sheet).
//
// Categories are referenced by `code` in action impact paths (e.g.
// "s1:fee-defense:vbank"). Codes are URL-safe slugs and stable once shipped.

export type CategoryPriority = "p0" | "p1" | "p2";

/**
 * Pattern-fit score against the fee-defense wedge. Derived from live metrics
 * (see `deriveFit`). Antipattern is a hard override per STRATEGY.md.
 *
 *   proven       confirmed wedge in data (volume + retention + habit)
 *   strong       high retention + meaningful volume — pattern replicates
 *   plausible    right shape but underbuilt — the strategic opportunity
 *   activation   high volume, weak retention — hooks but doesn't habit
 *   signal       small volume but high retention — worth watching
 *   weak         wrong shape against the wedge criteria
 *   antipattern  engagement-mint per STRATEGY.md — sunset, don't grow
 */
export type CategoryFit =
  | "proven"
  | "strong"
  | "plausible"
  | "activation"
  | "signal"
  | "target" // pattern fits but no live volume yet · strategic-intent flagged
  | "weak"
  | "antipattern";

/** How often the average customer touches this category. Drives target weight. */
export type Cadence = "daily" | "weekly" | "monthly" | "occasional" | "n/a";

/**
 * Strategic intent — the targets the ManCo commits to. Categories without
 * intent stay descriptive; categories WITH intent participate in the
 * distribution charts and the alignment view.
 */
export interface StrategicIntent {
  /** Target % of total earn-side issuance landing in this category. */
  earnTargetPct: number;
  /** Target % of total spend-side redemption landing in this category. */
  spendTargetPct: number;
  /** Touch cadence — drives why this category earns its target weight. */
  cadence: Cadence;
  /** One-line rationale shown next to the target in the distribution panel. */
  rationale: string;
}

/**
 * The fiat-useful threshold per category — the smallest coin balance that
 * lets a customer do ONE real-world thing in this category. Powers S2's
 * "% of users at fiat-useful balance" measurement.
 *
 *   coins      coin count required (e.g. 100 = ₦1,000 at 1 coin = ₦10)
 *   label      plain-English unit: "1 transfer fee", "1 airtime topup"
 *   todayPct   % of active users (365d) at or above this balance today
 *   targetPct  % we're committing to land within 12 months
 *   rationale  one-line per-category note shown in the S2 chart
 *
 * Gaming uses this slot differently — todayPct/targetPct measure "% of
 * users who triggered a gaming move this month" instead of a clearance %.
 * The `mode: "trigger"` flag tells the chart to label it that way.
 */
export interface FiatUsefulThreshold {
  coins: number;
  label: string;
  todayPct: number;
  targetPct: number;
  rationale: string;
  mode?: "clearance" | "trigger";
}

/**
 * D2 — Redemption attractiveness target: where the 14d return rate per
 * category needs to land. Today value comes from metrics.retain14dPct.
 */
export interface RetentionTarget {
  targetPct: number;
  rationale: string;
}

/**
 * S3 — Issuance cost target: who funds the coins minted into this category.
 * Bvndle's share is implied as (100 - consignedTargetPct). The partner is
 * the named consignment counterparty (UBA, MTN, NEPA, etc.).
 */
export interface ConsignmentTarget {
  consignedTodayPct: number;
  consignedTargetPct: number;
  partner: string;
  rationale: string;
}

/**
 * D3 — Redemption cost target: settlement latency in days + the rail used.
 * Instant = 0 days; T+1 = 1; manual reconciliation may be longer.
 */
export interface SettlementTarget {
  latencyTodayDays: number;
  latencyTargetDays: number;
  rail: string;
  rationale: string;
}

/**
 * Snapshot of live metrics per category. Numbers from
 * docs/../funnel-review-standalone.html, pulled 12 May 2026.
 *
 * `first365d`           — first-redeemers in trailing 365 days
 * `retain14dPct`        — % of first-redeemers who returned ≤14 days
 * `retainSameDayPct`    — % returned same day (the high-habit signal)
 * `everReturnedPct`     — % who ever returned (recurrence baseline)
 * `notes`               — short editorial caveat where the number is misleading
 */
export interface CategoryMetrics {
  first365d: number;
  retain14dPct: number;
  retainSameDayPct: number;
  everReturnedPct: number;
  notes?: string;
}

export interface Category {
  /** Stable code for impact paths and storage keys. URL-safe slug. */
  code: string;
  /** Display name. */
  label: string;
  /** One-line description shown under the row when expanded. */
  description: string;
  /** ManCo strategic priority — P0/P1/P2. */
  priority: CategoryPriority;
  /** Primary owner · supporting. Shown as a chip on the row. */
  owner: string;
  /** Optional anchor — the client/partner driving this category right now. */
  anchor?: string;
  /** Narration patterns matched in coinsLedger. Reference for SQL classifier. */
  narrationPatterns: string[];
  /** Live snapshot metrics (12 May 2026 — replaced by SQL in Phase 3). */
  metrics: CategoryMetrics;
  /**
   * Today's % share of total earn-side issuance (12 May 2026 snapshot).
   * Where unmeasured (Transport / Groceries / Pharmacies), it's 0 — the gap
   * is the strategic signal.
   */
  earnTodayPct?: number;
  /** Today's % share of total spend-side redemption (12 May 2026 snapshot). */
  spendTodayPct?: number;
  /** Strategic intent — ManCo target weights + rationale + cadence. */
  intent?: StrategicIntent;
  /** The fiat-useful threshold — powers S2's clearance metric. */
  threshold?: FiatUsefulThreshold;
  /** D2 — target 14d return rate per category. */
  retention?: RetentionTarget;
  /** S3 — target consignment mix per category. */
  consignment?: ConsignmentTarget;
  /** D3 — target settlement latency per category. */
  settlement?: SettlementTarget;
  /** True for categories not yet instrumented (no live classifier rows). */
  notInstrumented?: boolean;
  /** Override flag: hard-set as antipattern regardless of metrics. */
  antipattern?: boolean;
}

// ---------------------------------------------------------------------------
// The taxonomy — 11 partner-report categories (2 Jun 2026 sales-team
// ground truth). Replaces the old data-anchored mint-side cut. Same list
// shown under every KPI block on both supply and demand; what changes per
// block is the *metric* shown per row (see kpis.ts → categoryMetricKey).
//
// Live SQL bound: the live-spend-share-by-category /
// live-retention-14d-by-category / live-first-redeem-active-share queries
// in resources/cohorts/query-catalog.json now match merchant names to
// these partner codes via CASE WHEN narration ~* '...'. Snapshot fields
// (spendTodayPct etc.) remain as fallback when Metabase is unreachable.
//
// Sourced from:
//   - "Bvndle Redemption_Partners_Report End April 2026.docx"
//   - "1 pager_Bvndle Merchant LaaS Update Report.docx" (2 Jun 2026)
//
// Per-merchant detail (status, branches, monthly redemption) lives in
// src/lib/partners-current.ts.
// ---------------------------------------------------------------------------

export const CATEGORIES: Category[] = [
  // ----- 01 RETAIL · SUPERMARKETS (PRIMARY) -----
  {
    code: "supermarkets",
    label: "Retail · Supermarkets",
    description:
      "PRIMARY anchor — everyday essential spending. 3/3 onboarded (Jendol, Trolleys, Market Square). Jendol alone drives 17% of YTD redemption.",
    priority: "p0",
    owner: "Williams · Ufuoma (high-redemption status)",
    anchor: "Jendol Superstores · regional, 10/12 branches live",
    narrationPatterns: ["supermarket", "jendol", "market square", "trolleys"],
    metrics: { first365d: 4200, retain14dPct: 48.0, retainSameDayPct: 22.0, everReturnedPct: 62.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 19.1,
    intent: {
      earnTargetPct: 22,
      spendTargetPct: 20,
      cadence: "daily",
      rationale:
        "PRIMARY · daily-cadence merchant pattern. Jendol's 17% solo YTD share proves the pull. Push earn-at-spend integration so coins are minted where they're spent.",
    },
    threshold: {
      coins: 100,
      label: "1 basket discount (₦1,000)",
      todayPct: 12,
      targetPct: 45,
      rationale:
        "Weekly basket cadence. A modest ₦1,000 discount is fiat-useful for groceries.",
    },
    retention: {
      targetPct: 55,
      rationale:
        "Groceries are habit-formable. Jendol's branch density + weekly cadence supports 55% 14d return.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 50,
      partner: "Supermarket POS loyalty programmes",
      rationale:
        "Retailers already run loyalty budgets · negotiate consignment of those rewards to Bvndle.",
    },
    settlement: {
      latencyTodayDays: 1,
      latencyTargetDays: 0,
      rail: "POS rail (T+1 → instant on integration)",
      rationale: "T+1 today via POS settlement. Target instant via card-network direct-pay.",
    },
    notInstrumented: false,
  },

  // ----- 02 AUTOMOTIVE · MOBILITY -----
  {
    code: "automotive",
    label: "Automotive · Mobility",
    description:
      "Fuel + transport — high-frequency utility. 2/3 onboarded (Nepal Energies, BvndleGo). Ardova in Value-Prop → pre-funded model is the consignment proof-point.",
    priority: "p0",
    owner: "Williams · Ruki (BvndleGo)",
    anchor: "Ardova Plc · pre-funded model proof",
    narrationPatterns: ["fuel", "ardova", "nepal", "petrol", "transport", "bvndlego"],
    metrics: { first365d: 800, retain14dPct: 38.0, retainSameDayPct: 14.0, everReturnedPct: 52.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 5.7,
    intent: {
      earnTargetPct: 18,
      spendTargetPct: 15,
      cadence: "daily",
      rationale:
        "Daily-cadence utility. Replicating Ardova's pre-funded model across the fuel network shifts both earn distribution and funding mix.",
    },
    threshold: {
      coins: 300,
      label: "1 fuel top-up (~₦3,000)",
      todayPct: 4,
      targetPct: 30,
      rationale: "Daily fill-up is the unit. ₦3k is one tank's worth of useful subsidy.",
    },
    retention: {
      targetPct: 45,
      rationale:
        "Daily-cadence pattern. Once a user fills up at Nepal once, the lifetime repeat is high.",
    },
    consignment: {
      consignedTodayPct: 10,
      consignedTargetPct: 60,
      partner: "Ardova Plc (pre-funded), Total Energies (target)",
      rationale:
        "Ardova already pre-funds. Replicate via Total + Eterna once the Pentagon licence clears (Oct 2026).",
    },
    settlement: {
      latencyTodayDays: 0,
      latencyTargetDays: 0,
      rail: "Pre-funded (instant) for fuel; merchant rail for BvndleGo",
      rationale: "Pre-funded fuel = instant. BvndleGo rideshare needs its own rail buildout.",
    },
    notInstrumented: false,
  },

  // ----- 03 HEALTH & WELLNESS -----
  {
    code: "health",
    label: "Health & Wellness",
    description:
      "Fitness + pharmacy + personal care. 2/3 onboarded (i-Fitness 29/30 branches, Ace Beauty Bar). Medplus stuck at mgmt-alignment caps 110-branch pharmacy depth.",
    priority: "p1",
    owner: "Seye (Medplus LaaS) · Williams",
    anchor: "i-Fitness · 29/30 branches live",
    narrationPatterns: ["fitness", "i-fitness", "medplus", "pharmacy", "gym"],
    metrics: { first365d: 320, retain14dPct: 42.0, retainSameDayPct: 12.0, everReturnedPct: 55.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 0.9,
    intent: {
      earnTargetPct: 8,
      spendTargetPct: 10,
      cadence: "monthly",
      rationale:
        "Gym memberships are sticky once entered. Pharmacy is monthly cadence essential. Unblock Medplus to unlock category scale.",
    },
    threshold: {
      coins: 500,
      label: "1 pharmacy basket (~₦5,000)",
      todayPct: 3,
      targetPct: 25,
      rationale: "Monthly meds top-up. ₦5k is one fiat-useful pharmacy visit.",
    },
    retention: {
      targetPct: 40,
      rationale:
        "Subscription pattern. i-Fitness members are recurring by default; pharmacy follows monthly health cadence.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 35,
      partner: "Medplus LaaS, gym chain rewards",
      rationale:
        "Medplus is the LaaS proof-point once mgmt-alignment closes. Gym memberships already subsidise retention budgets.",
    },
    settlement: {
      latencyTodayDays: 1,
      latencyTargetDays: 0,
      rail: "POS rail (T+1 → instant on integration)",
      rationale: "Standard POS T+1. Pharmacy can integrate directly.",
    },
    notInstrumented: false,
  },

  // ----- 04 DINING · RESTAURANTS -----
  {
    code: "dining",
    label: "Dining · Restaurants",
    description:
      "Food + beverage. 2 priority + 4 standard live. CONCENTRATION RISK · 46% of YTD redemption sits here (El Padrino + Ofada Hut alone are 40%).",
    priority: "p0",
    owner: "Williams · CX + PSL (Glovo)",
    anchor: "El Padrino · ₦106k YTD, top single-merchant redeemer",
    narrationPatterns: ["restaurant", "padrino", "ofada", "yellow chilli", "burger king"],
    metrics: { first365d: 7800, retain14dPct: 34.0, retainSameDayPct: 18.0, everReturnedPct: 58.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 45.7,
    intent: {
      earnTargetPct: 15,
      spendTargetPct: 12,
      cadence: "weekly",
      rationale:
        "Over-indexes at 46% of YTD redemption · concentration risk. Push to 12% by broadening to Burger King SLA + Olive/Panarottis + Chicken Republic.",
    },
    threshold: {
      coins: 200,
      label: "1 meal discount (₦2,000)",
      todayPct: 22,
      targetPct: 45,
      rationale: "Weekly meal out is the unit. ₦2k is one fiat-useful discount.",
    },
    retention: {
      targetPct: 40,
      rationale:
        "Ofada Hut's 32k → 6k Mar-May decay flags a retention strategy gap. Habit lives in daily-cadence dining (lunch run), not weekly treat-out.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 40,
      partner: "QSR chain marketing budgets",
      rationale: "Chains like Burger King + Chicken Republic run marketing spend that can be channelled.",
    },
    settlement: {
      latencyTodayDays: 1,
      latencyTargetDays: 0,
      rail: "POS rail (T+1) or instant via card",
      rationale: "T+1 POS today. Card-based dining → instant.",
    },
    notInstrumented: false,
  },

  // ----- 05 BEAUTY & PERSONAL CARE -----
  {
    code: "beauty",
    label: "Beauty & Personal Care",
    description:
      "Lifestyle grooming + beauty retail. OVER-TARGET at 4/3 onboarded (Dang Lifestyle 7/7 branches). Strongest sales execution category to date.",
    priority: "p1",
    owner: "Williams",
    anchor: "Dang Lifestyle · 7/7 branches live",
    narrationPatterns: ["beauty", "dang", "fragrance", "spa", "cnm essentials"],
    metrics: { first365d: 620, retain14dPct: 30.0, retainSameDayPct: 8.0, everReturnedPct: 45.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 3.5,
    intent: {
      earnTargetPct: 10,
      spendTargetPct: 10,
      cadence: "weekly",
      rationale:
        "Over-execution category. Push Montaigne SLA + Ginger close to unlock 36+ branch depth on top of existing wins.",
    },
    threshold: {
      coins: 200,
      label: "1 small beauty buy (₦2,000)",
      todayPct: 6,
      targetPct: 30,
      rationale: "Weekly grooming buy. ₦2k is one fiat-useful skincare or fragrance item.",
    },
    retention: {
      targetPct: 30,
      rationale: "Weekly cadence with lower stickiness than groceries. Push via category-specific nudges.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 25,
      partner: "Brand co-op marketing",
      rationale: "Premium brand portfolios already run co-op marketing.",
    },
    settlement: {
      latencyTodayDays: 1,
      latencyTargetDays: 0,
      rail: "POS rail (T+1) or online",
      rationale: "POS or online · standard rails.",
    },
    notInstrumented: false,
  },

  // ----- 06 E-COMMERCE · DIGITAL SERVICES -----
  {
    code: "ecommerce",
    label: "E-Commerce · Digital Services",
    description:
      "Online platforms with broad reach. 2/3 onboarded (Pricepally, Konga). Pricepally hit ₦18k in May alone — under-utilised given platform reach.",
    priority: "p0",
    owner: "Williams · PSL (LaaS)",
    anchor: "Pricepally · global online",
    narrationPatterns: ["pricepally", "konga", "ecommerce", "online"],
    metrics: { first365d: 1500, retain14dPct: 52.0, retainSameDayPct: 18.0, everReturnedPct: 68.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 5.6,
    intent: {
      earnTargetPct: 12,
      spendTargetPct: 12,
      cadence: "weekly",
      rationale:
        "Online rail = instant settlement, repeat-shopping pattern. Konga onboarded but underutilised · traffic-driver in-app placement is the lever.",
    },
    threshold: {
      coins: 100,
      label: "1 online add-to-cart (₦1,000)",
      todayPct: 9,
      targetPct: 35,
      rationale: "Online weekly shop is the unit. ₦1k is one fiat-useful subsidy.",
    },
    retention: {
      targetPct: 55,
      rationale: "Online shopping has best return shape outside daily-cadence. Push via Glovo activation.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 30,
      partner: "Marketplace partner subsidies",
      rationale: "Konga + Pricepally can subsidise via partner-fund campaigns.",
    },
    settlement: {
      latencyTodayDays: 0,
      latencyTargetDays: 0,
      rail: "Online rail (instant)",
      rationale: "Instant by default · online merchants are rail-ready.",
    },
    notInstrumented: false,
  },

  // ----- 07 TECHNOLOGY & ELECTRONICS -----
  {
    code: "tech",
    label: "Technology & Electronics",
    description:
      "Consumer tech retail. 2/3 onboarded (Festajei, Garamart). Samsung + Slot + SIMS untouched — high-reach prospects (30-80 branches each).",
    priority: "p2",
    owner: "Williams",
    anchor: "Festajei Electronics · 3/3 branches live",
    narrationPatterns: ["festajei", "garamart", "samsung", "slot", "electronics"],
    metrics: { first365d: 55, retain14dPct: 12.0, retainSameDayPct: 2.0, everReturnedPct: 25.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 0.3,
    intent: {
      earnTargetPct: 3,
      spendTargetPct: 4,
      cadence: "occasional",
      rationale:
        "High-ticket episodic category. First outreach to Samsung + Slot would step-change branch coverage.",
    },
    threshold: {
      coins: 2000,
      label: "1 small device discount (₦20,000)",
      todayPct: 1,
      targetPct: 12,
      rationale: "High-ticket category. ₦20k = one fiat-useful device discount.",
    },
    retention: {
      targetPct: 15,
      rationale: "Episodic by definition. Don't chase retention here — chase first-redemption value.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 50,
      partner: "OEM co-op marketing",
      rationale: "High-margin retailers + OEM co-op routinely subsidise loyalty.",
    },
    settlement: {
      latencyTodayDays: 1,
      latencyTargetDays: 0,
      rail: "POS rail (T+1)",
      rationale: "T+1 today. Card-direct = instant target.",
    },
    notInstrumented: false,
  },

  // ----- 08 HOSPITALITY & TRAVEL -----
  {
    code: "hospitality",
    label: "Hospitality & Travel",
    description:
      "Hotels + serviced apartments + travel. 2/3 onboarded (Boardroom Apt, Geo Travels). Amber Hospitality x Bvndle proposal in review (Ayo Tifase).",
    priority: "p1",
    owner: "Williams · Ayo (Amber Hospitality deck in review)",
    anchor: "Boardroom Apartment · Lagos",
    narrationPatterns: ["hotel", "boardroom", "presken", "delborough", "travel"],
    metrics: { first365d: 95, retain14dPct: 14.0, retainSameDayPct: 1.0, everReturnedPct: 22.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 0.8,
    intent: {
      earnTargetPct: 6,
      spendTargetPct: 8,
      cadence: "occasional",
      rationale:
        "Aspirational category. Closing Delborough + Presken would round out the priority three. Amber deck (Ayo) is the strategic open thread.",
    },
    threshold: {
      coins: 3000,
      label: "1 night discount (~₦30,000)",
      todayPct: 1,
      targetPct: 10,
      rationale: "Episodic high-value. ₦30k = one fiat-useful hotel-night subsidy.",
    },
    retention: {
      targetPct: 15,
      rationale: "Occasional usage by nature. Push value-per-redemption not frequency.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 60,
      partner: "Hotel loyalty programmes",
      rationale: "Hotels already run loyalty budgets — negotiate consignment.",
    },
    settlement: {
      latencyTodayDays: 7,
      latencyTargetDays: 1,
      rail: "Manual / bespoke today",
      rationale: "Fully bespoke today · weeks of reconciliation. Build standard hotel rail.",
    },
    notInstrumented: false,
  },

  // ----- 09 FASHION & ACCESSORIES -----
  {
    code: "fashion",
    label: "Fashion & Accessories",
    description:
      "Apparel + lifestyle fashion. 1/3 priority onboarded but 4ey Fashion (standard list) drives ₦28k YTD from a single store — sleeper category.",
    priority: "p1",
    owner: "Williams",
    anchor: "4ey Fashion · single store, ₦28k YTD",
    narrationPatterns: ["fashion", "4ey", "ruff tumble", "twice as nice"],
    metrics: { first365d: 380, retain14dPct: 28.0, retainSameDayPct: 6.0, everReturnedPct: 42.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 7.1,
    intent: {
      earnTargetPct: 5,
      spendTargetPct: 5,
      cadence: "monthly",
      rationale:
        "4ey punches above weight. Use it as proof to close Ruff n Tumble + Twice As Nice from Value Prop.",
    },
    threshold: {
      coins: 500,
      label: "1 apparel buy (~₦5,000)",
      todayPct: 4,
      targetPct: 22,
      rationale: "Monthly apparel buy. ₦5k = one fiat-useful clothing piece discount.",
    },
    retention: {
      targetPct: 30,
      rationale: "Monthly cadence. 4ey's volume suggests the category will return if expanded.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 30,
      partner: "Fashion-house GTM budgets",
      rationale: "Branded fashion-houses run seasonal marketing budgets to subsidise loyalty.",
    },
    settlement: {
      latencyTodayDays: 1,
      latencyTargetDays: 0,
      rail: "POS / online (T+1 → instant)",
      rationale: "Standard rails. Online fashion = instant.",
    },
    notInstrumented: false,
  },

  // ----- 10 ENTERTAINMENT & EXPERIENCES -----
  {
    code: "entertainment",
    label: "Entertainment & Experiences",
    description:
      "Cinema + leisure. 1/3 priority onboarded (Carven, Breather). Filmhouse integration BLOCKED — 12-branch unlock waiting. Herel Play (standard) drives 11% of YTD on its own.",
    priority: "p1",
    owner: "Williams · Seye (Filmhouse)",
    anchor: "Herel Play · ₦42k YTD",
    narrationPatterns: ["filmhouse", "carven", "herel play", "entertainment", "ebonylife"],
    metrics: { first365d: 1600, retain14dPct: 26.0, retainSameDayPct: 6.0, everReturnedPct: 38.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 11.3,
    intent: {
      earnTargetPct: 6,
      spendTargetPct: 6,
      cadence: "weekly",
      rationale:
        "Cinema + leisure is weekly cadence. Filmhouse unblock would triple branch coverage and broaden the redemption pattern.",
    },
    threshold: {
      coins: 200,
      label: "1 cinema ticket (~₦2,000)",
      todayPct: 5,
      targetPct: 25,
      rationale: "Weekly leisure buy. ₦2k = one fiat-useful ticket/experience.",
    },
    retention: {
      targetPct: 30,
      rationale: "Herel Play shows habit-decay pattern. Push value over frequency.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 40,
      partner: "Cinema network marketing",
      rationale: "Filmhouse / Ebonylife / cinema chains have ticket-marketing budgets.",
    },
    settlement: {
      latencyTodayDays: 0,
      latencyTargetDays: 0,
      rail: "Instant (digital tickets)",
      rationale: "Cinema is digital-instant.",
    },
    notInstrumented: false,
  },

  // ----- 11 GIFTING -----
  {
    code: "gifting",
    label: "Gifting",
    description:
      "Flowers + occasion retail. 2/3 onboarded (Regal Flowers, Scribbles) but zero volume YTD. Williams' gift-card strategy under review.",
    priority: "p2",
    owner: "Williams (Bvndle Gift Cards strategy)",
    anchor: "Regal Flowers + Scribbles · live, no volume yet",
    narrationPatterns: ["gift", "flowers", "regal", "scribbles"],
    metrics: { first365d: 45, retain14dPct: 18.0, retainSameDayPct: 4.0, everReturnedPct: 28.0 },
    earnTodayPct: 0.0,
    spendTodayPct: 0.0,
    intent: {
      earnTargetPct: 2,
      spendTargetPct: 2,
      cadence: "occasional",
      rationale:
        "Zero YTD volume despite 2 anchors live. Williams' gift-card strategy will decide if this gets a dedicated GTM push or stays as attach to other categories.",
    },
    threshold: {
      coins: 500,
      label: "1 gift voucher (~₦5,000)",
      todayPct: 0,
      targetPct: 10,
      rationale: "Episodic high-touch. ₦5k = one fiat-useful gift-card load.",
    },
    retention: {
      targetPct: 20,
      rationale: "Occasion cadence — birthdays / anniversaries / holidays.",
    },
    consignment: {
      consignedTodayPct: 0,
      consignedTargetPct: 60,
      partner: "Gift-card margin-funding model",
      rationale: "Gift-card economics already carry inherent margin to subsidise.",
    },
    settlement: {
      latencyTodayDays: 0,
      latencyTargetDays: 0,
      rail: "Instant (online + physical)",
      rationale: "Digital gift cards instant. Physical flowers via online rail.",
    },
    notInstrumented: false,
  },
];

/**
 * Earn-side issuance source that isn't a category — the dominant earn channel
 * today. Surfaced specifically on the S1 distribution chart so the eye sees
 * "98% of earn is signup_bonus → target 15%, spread the remaining 85% into
 * categories that recur".
 */
export const SIGNUP_BONUS_SOURCE = {
  code: "signup-bonus",
  label: "signup_bonus",
  earnTodayPct: 98.0,
  earnTargetPct: 10.0,
  rationale: "A one-off coin when someone joins. Useful at signup · but if 98% of all earn comes from here, the customer never earns again. Drop it to make room for recurring earn.",
};

// ---------------------------------------------------------------------------
// Fit derivation — the rule the ManCo can argue with, not a hand-coded label.
//
// The fee-defense criteria are:
//   - Recurring  → high `everReturnedPct` (customers come back at all)
//   - Habit-formable → high `retain14dPct` (the door closes fast — 14d is
//                                            where lifecycle/CRM has leverage)
//   - Meaningful volume → `first365d` provides scale
//
// Thresholds chosen to match the funnel-review's strategic reads. Adjust
// here if the ManCo recalibrates; every category re-scores automatically.
// ---------------------------------------------------------------------------

const FIT_RULES = {
  proven: { minFirst: 5000, minRetain14d: 50 },      // big vol + habit-formable
  strong: { minFirst: 150, minRetain14d: 70 },        // small-mid vol but excellent retention
  plausible: { minFirst: 40, minRetain14d: 35 },      // right shape, underbuilt
  activation: { minFirst: 1000, maxRetain14d: 30 },   // big vol, weak retention
  signal: { minFirst: 0, minRetain14d: 65 },          // tiny vol but signals fit
};

export function deriveFit(cat: Category): CategoryFit {
  if (cat.antipattern) return "antipattern";
  // Categories with strategic intent but no live data — "right shape,
  // unbuilt". Distinguishes Transport / Groceries / Pharmacies (we want
  // these) from "Other" (we don't yet know).
  if (cat.notInstrumented && cat.intent && cat.intent.earnTargetPct + cat.intent.spendTargetPct > 0)
    return "target";
  const m = cat.metrics;
  if (m.first365d >= FIT_RULES.proven.minFirst && m.retain14dPct >= FIT_RULES.proven.minRetain14d)
    return "proven";
  if (
    m.first365d >= FIT_RULES.activation.minFirst &&
    m.retain14dPct < FIT_RULES.activation.maxRetain14d
  )
    return "activation";
  if (m.first365d >= FIT_RULES.strong.minFirst && m.retain14dPct >= FIT_RULES.strong.minRetain14d)
    return "strong";
  if (m.first365d >= FIT_RULES.plausible.minFirst && m.retain14dPct >= FIT_RULES.plausible.minRetain14d)
    return "plausible";
  if (m.retain14dPct >= FIT_RULES.signal.minRetain14d) return "signal";
  return "weak";
}

export const FIT_LABEL: Record<CategoryFit, string> = {
  proven: "PROVEN",
  strong: "STRONG",
  plausible: "PLAUSIBLE",
  target: "TARGET",
  activation: "ACTIVATION TRAP",
  signal: "SIGNAL",
  weak: "WEAK",
  antipattern: "ANTIPATTERN",
};

export const FIT_COLOR: Record<CategoryFit, string> = {
  proven: "var(--purple)",
  strong: "#5ec98c",
  plausible: "var(--amber)",
  target: "#4fb3ff",
  activation: "var(--coral)",
  signal: "var(--purple)",
  weak: "var(--ink-faint)",
  antipattern: "var(--coral)",
};

export const PRIORITY_LABEL: Record<CategoryPriority, string> = {
  p0: "P0",
  p1: "P1",
  p2: "P2",
};

export const PRIORITY_COLOR: Record<CategoryPriority, string> = {
  p0: "var(--purple)",
  p1: "var(--amber)",
  p2: "var(--ink-faint)",
};

export function findCategory(code: string): Category | undefined {
  return CATEGORIES.find((c) => c.code === code);
}

// ---------------------------------------------------------------------------
// Effort per category — extracted from action impact paths.
//
// Impacts encode the path: "s1:fee-defense:vbank" means S1 → fee-defense →
// vbank. The middle segment is the category code. This function counts the
// number of actions touching a category under a given KPI (or any KPI).
// ---------------------------------------------------------------------------

export function categoriesFromImpacts(impacts: string[]): string[] {
  const codes = new Set<string>();
  for (const i of impacts) {
    const parts = i.split(":");
    if (parts.length >= 2 && parts[1]) codes.add(parts[1]);
  }
  return Array.from(codes);
}
